import React, { useCallback, useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import type { EditorEvents, Extensions } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import styles from "./RichTextEditor.module.css";

import MenuBar from "./MenuBar";
import LinkPopover from "./LinkPopover";
import { RICH_TEXT_PARSE_OPTIONS } from "./RichTextEditor.utils";

type RichTextEditorProps = {
    content?: string;
    placeholder?: string;
    enableContentCheck?: boolean;
    onContentError?: (error: EditorEvents["contentError"]) => void;
    onContentUpdate?: (update: EditorEvents["update"]) => void;
    onEditorCreate?: (create: EditorEvents["create"]) => void;
    onBlur?: (blur: EditorEvents["blur"]) => void;
    characterLimit?: number;
    disabled?: boolean;
    className?: string;
};

const RichTextEditor: React.FC<RichTextEditorProps> = (props) => {
    const [isFocused, setIsFocused] = useState(false);
    const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);

    const toolbarRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const toggleLinkPopover = useCallback(() => setLinkPopoverOpen((prev) => !prev), []);
    const closeLinkPopover = useCallback(() => setLinkPopoverOpen(false), []);

    const extensions: Extensions = [
        StarterKit,
        Underline,
        Highlight,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        Link.configure({ openOnClick: true, autolink: true, defaultProtocol: "https" })
    ];

    if (props.placeholder) {
        extensions.push(Placeholder.configure({ placeholder: props.placeholder }));
    }

    const editor = useEditor({
        extensions,
        content: props.content || "<p></p>",
        parseOptions: RICH_TEXT_PARSE_OPTIONS,
        enableContentCheck: props.enableContentCheck ?? true,
        onContentError: props.onContentError,
        onUpdate: props.onContentUpdate,
        onCreate: props.onEditorCreate,
        onBlur: props.onBlur,
        editable: props.disabled !== true
    });

    useEffect(() => {
        if (!editor) return;
        const handleFocus = () => setIsFocused(true);
        const handleBlur = () => setIsFocused(false);
        editor.on("focus", handleFocus);
        editor.on("blur", handleBlur);
        return () => {
            editor.off("focus", handleFocus);
            editor.off("blur", handleBlur);
        };
    }, [editor]);

    useEffect(() => {
        const handler = (e: PointerEvent) => {
            const target = e.target as Node;
            if (toolbarRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
            setLinkPopoverOpen(false);
        };
        document.addEventListener("pointerdown", handler);
        return () => document.removeEventListener("pointerdown", handler);
    }, []);

    if (!editor) return null;

    const htmlLength = editor.getHTML().length;
    const htmlLimitPercent = props.characterLimit ? Math.round((100 / props.characterLimit) * htmlLength) : 0;

    return (
        <div className={`${styles.root} ${props.className || ""}`}>
            <div ref={toolbarRef}>
                <MenuBar editor={editor} expanded={isFocused} onToggleLinkPopover={toggleLinkPopover} />
            </div>

            <EditorContent editor={editor} className={props.disabled ? styles.richTextEditorReadonly : ""} />

            <LinkPopover editor={editor} open={linkPopoverOpen} onClose={closeLinkPopover} popoverRef={popoverRef} />

            {props.characterLimit && (
                <div
                    className={`${styles.characterCount} ${
                        htmlLength > props.characterLimit ? styles.characterCountWarning : ""
                    }`}
                >
                    <svg height="20" width="20" viewBox="0 0 20 20">
                        <circle r="10" cx="10" cy="10" fill="#e9ecef" />
                        <circle
                            r="5"
                            cx="10"
                            cy="10"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="10"
                            strokeDasharray={`calc(${htmlLimitPercent} * 31.4 / 100) 31.4`}
                            transform="rotate(-90) translate(-20)"
                        />
                        <circle r="6" cx="10" cy="10" fill="white" />
                    </svg>
                    {htmlLength} / {props.characterLimit} HTML-characters
                </div>
            )}
        </div>
    );
};

export default RichTextEditor;
